import cx from 'classnames';
import Link, { LinkProps } from 'next/link';
import { forwardRef, type ElementType, type ReactNode } from 'react';
import css from './Anchor.module.scss';

export type AnchorVariant =
  | 'anchor'
  | 'button-outline'
  | 'button-outline-light'
  | 'button-solid'
  | 'button-transparent';

export interface IAnchorProps extends LinkProps {
  children: ReactNode;
  className?: string;
  color?: 'blue' | 'orange';
  icon?: ElementType;
  iconSize?: number;
  isDisabled?: boolean;
  rel?: string;
  tabIndex?: number;
  target?: string;
  title?: string;
  variant?: AnchorVariant;
  size?: 'sm' | 'md' | 'lg';
  style?: { color: string };
}

export const Anchor = forwardRef<HTMLAnchorElement, IAnchorProps>(
  (
    {
      className,
      children,
      color = 'blue',
      href,
      icon: Icon,
      iconSize = 14,
      isDisabled,
      variant = 'anchor',
      size = 'md',
      style,
      ...rest
    },
    ref
  ) => {
    const renderChildren = () => (
      <>
        {!!Icon && <Icon className="mr-1" height={iconSize} width={iconSize} />}
        {children}
      </>
    );

    return isDisabled ? (
      <span
        className={cx(
          css.anchor,
          css.disabled,
          css[color],
          css[variant],
          css[size],
          className
        )}
        style={style}
      >
        {renderChildren()}
      </span>
    ) : (
      <Link
        ref={ref}
        className={cx(
          css.anchor,
          css[color],
          css[variant],
          css[size],
          className
        )}
        href={href ?? ''}
        legacyBehavior={false}
        rel={rest.target === '_blank' ? 'noopener noreferrer' : undefined}
        style={style}
        {...rest}
      >
        {renderChildren()}
      </Link>
    );
  }
);

Anchor.displayName = 'Anchor';
